import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, ExternalLink, BarChart3 } from "lucide-react";

export default function InvestorUpdatesAdmin() {
    const [projects, setProjects] = useState([]);
    const [selected, setSelected] = useState("");
    const [updates, setUpdates] = useState([]);
    const [engagement, setEngagement] = useState([]);
    const [form, setForm] = useState({ title: "", body: "", include_snapshot: true });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/projects").then(({ data }) => {
            setProjects(data.items || []);
            if (data.items?.[0]) setSelected(data.items[0].id);
        });
        api.get("/analytics/investor-engagement").then(({ data }) => setEngagement(data.items || []));
    }, []);

    const loadUpdates = (pid) => {
        if (!pid) { setUpdates([]); return; }
        api.get(`/projects/${pid}/investor-updates`).then(({ data }) => setUpdates(data.items || []));
    };

    useEffect(() => { loadUpdates(selected); }, [selected]);

    const create = async (e) => {
        e.preventDefault();
        if (!selected) return;
        setBusy(true);
        try {
            await api.post(`/projects/${selected}/investor-updates`, {
                project_id: selected, title: form.title, body: form.body,
                include_snapshot: form.include_snapshot, make_public_link: true,
            });
            setForm({ title: "", body: "", include_snapshot: true });
            toast.success("Update created (draft)");
            loadUpdates(selected);
        } catch { toast.error("Failed"); }
        setBusy(false);
    };

    const publish = async (uid) => {
        try {
            const { data } = await api.post(`/investor-updates/${uid}/publish`, { send_email: true });
            toast.success(`Published — ${data.emails_queued} email(s) queued`);
            loadUpdates(selected);
            api.get("/analytics/investor-engagement").then(({ data }) => setEngagement(data.items || []));
        } catch { toast.error("Publish failed"); }
    };

    const copyPublicLink = (u) => {
        if (!u.public_token) return toast.error("No public token");
        const url = `${window.location.origin}/public/investor-update?u=${u.id}&t=${u.public_token}`;
        navigator.clipboard.writeText(url);
        toast.success("Public link copied");
    };

    return (
        <div className="p-6" data-testid="investor-updates-admin">
            <div className="mb-6">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Investor updates</div>
                <h1 className="text-2xl tracking-tight mt-1">Compose & publish</h1>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <form onSubmit={create} className="lg:col-span-2 border border-border p-4 space-y-3">
                    <div>
                        <label className="text-xs uppercase tracking-widest text-muted-foreground">Project</label>
                        <select value={selected} onChange={(e) => setSelected(e.target.value)} className="mt-1 w-full border border-border bg-background px-3 py-2 text-sm" data-testid="update-project-select">
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-muted-foreground">Title</label>
                        <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 w-full border border-border bg-background px-3 py-2 text-sm" data-testid="update-title-input" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-muted-foreground">Body</label>
                        <textarea required rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-1 w-full border border-border bg-background px-3 py-2 text-sm" data-testid="update-body-input" />
                    </div>
                    <button disabled={busy} className="bg-foreground text-background px-3 py-2 text-xs" data-testid="update-create-btn">
                        {busy ? "Creating…" : "Create draft"}
                    </button>
                </form>

                <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Drafts & published</div>
                    <div className="space-y-2">
                        {updates.map((u) => (
                            <div key={u.id} className="border border-border p-3" data-testid={`update-${u.id}`}>
                                <div className="text-sm font-medium">{u.title}</div>
                                <div className="text-xs text-muted-foreground">{u.status} · {u.created_at?.slice(0, 10)}</div>
                                <div className="mt-2 flex gap-2">
                                    {u.status === "draft" && (
                                        <button onClick={() => publish(u.id)} className="text-xs underline flex items-center gap-1" data-testid={`publish-${u.id}`}><Send size={10} /> Publish</button>
                                    )}
                                    {u.public_token && u.status === "published" && (
                                        <button onClick={() => copyPublicLink(u)} className="text-xs underline flex items-center gap-1"><ExternalLink size={10} /> Copy link</button>
                                    )}
                                </div>
                            </div>
                        ))}
                        {updates.length === 0 && <div className="text-xs text-muted-foreground">No updates yet.</div>}
                    </div>
                </div>
            </div>

            <div className="mt-10">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1"><BarChart3 size={12} /> Engagement</div>
                <div className="border border-border overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-secondary/40">
                            <tr>
                                <th className="text-left p-2 font-normal text-xs uppercase text-muted-foreground">Update</th>
                                <th className="text-left p-2 font-normal text-xs uppercase text-muted-foreground">Project</th>
                                <th className="text-right p-2 font-normal text-xs uppercase text-muted-foreground">Opens</th>
                                <th className="text-right p-2 font-normal text-xs uppercase text-muted-foreground">Avg Dwell</th>
                                <th className="text-right p-2 font-normal text-xs uppercase text-muted-foreground">Questions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {engagement.map((e) => (
                                <tr key={e.id} data-testid={`engagement-${e.id}`}>
                                    <td className="p-2">{e.title}</td>
                                    <td className="p-2 text-muted-foreground">{e.project_name}</td>
                                    <td className="p-2 text-right">{e.opens}</td>
                                    <td className="p-2 text-right">{e.avg_dwell_seconds}s</td>
                                    <td className="p-2 text-right">{e.question_count}</td>
                                </tr>
                            ))}
                            {engagement.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-muted-foreground text-xs">No engagement recorded yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
